/**
 * Scene-selection contract — WP-2 (Content catalog and scene tree refinement).
 *
 * This module defines the **stable selection contract** that every
 * future layer-family ToolCard (WPs 3..10 in the
 * `VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md`) must consume.
 * Today only the `SceneTreePanel` writes selections; tomorrow the
 * `ToolCardDock`, the `SemanticPropertyCard`, and the engine pickers
 * read from the same shape.
 *
 * The selection is intentionally a tagged union with optional cross-
 * references so a single selection can simultaneously identify:
 *   - the unified scene-tree node (`nodeId` / `indexNodeId`)
 *   - the renderable layer (`layerId`)
 *   - the canonical scene-package item (`sceneItemId`)
 *   - an inner feature within a layer (`featureId`)
 *   - the layer family (`layerKind`) and tool-card family
 *     (`toolCardKind`) used by the right-rail ToolCardDock
 *
 * The viewer's existing `Selection` type from `@geosoft/viewer-sdk` is a
 * narrower engine-picker contract (`{ layerId, featureId?, point? }`)
 * and stays in place for engine-side hit-test events. `SceneSelection`
 * is the chrome-side contract that drives ToolCards and the property
 * panel; the two coexist intentionally.
 *
 * Spec references:
 *   - `app-platform-gtwin/docs/SCENE_CONTRACT.md` — slot / layerKind /
 *     scene-item id semantics.
 *   - `app-platform-gtwin/docs/SCENE_INDEX_CONTRACT.md` — index node
 *     id semantics and `geometryRefs[].featureId` pointer.
 *   - `geosoft-platform-design-system/docs/UIUX_DETAILED_SURFACE_STUDY.md`
 *     — selection synchronization across map/3D/section/AI surfaces.
 */

import type { Layer } from "@geosoft/viewer-sdk";
import type { UnifiedSceneTreeNode } from "./scenePackage";
import {
  getToolCardKindFor,
  type ToolCardKind,
} from "./sceneExplorer";

// ---------------------------------------------------------------------------
// Type contract
// ---------------------------------------------------------------------------

/** Discriminator for the kind of selection. */
export type SceneSelectionKind =
  | "cleared"
  | "layer"
  | "indexNode"
  | "sceneItem"
  | "feature";

/**
 * The single selection contract used across the viewer chrome. Every
 * field besides `kind` is optional so a partial selection (e.g. just a
 * scene-item id during deep-link hydration) is still well-typed.
 */
export interface SceneSelection {
  readonly kind: SceneSelectionKind;
  /** Scene-tree node id (alias of `indexNodeId` when the node lives in
   *  the scene-index graph; else a synthesized id). */
  readonly nodeId?: string;
  /** Renderable `Layer.id` from `@geosoft/viewer-sdk`. */
  readonly layerId?: string;
  /** Inner feature id (e.g. BIM `IfcGlobalId` or vector feature id). */
  readonly featureId?: string | number;
  /** GeoJSON / vector feature attributes from the last engine pick (G28). */
  readonly featureProperties?: Readonly<Record<string, unknown>>;
  /** Canonical scene-package `SceneItem.id`. */
  readonly sceneItemId?: string;
  /** Scene-index graph node id (always equals `nodeId` for index-driven
   *  trees; missing when only a layer was selected). */
  readonly indexNodeId?: string;
  /** Layer kind from `@geosoft/viewer-sdk` `LayerKind` (`raster`,
   *  `vector`, `bim`, `pointcloud`, `3dtiles`, `terrain`,
   *  `sensor`/`timeseries`, …). */
  readonly layerKind?: string;
  /** Stable tool-card family (`basemap`, `raster`, `terrain`, …) the
   *  right-rail ToolCardDock should activate. See
   *  `sceneExplorer.getToolCardKindFor`. */
  readonly toolCardKind?: ToolCardKind;
  /** Active panorama-bubble station id when a bubble layer or station
   *  node is selected from the scene tree (LoR7). */
  readonly panoramaStationId?: string;
  /** Optional right-rail detail surface requested by an explicit row action. */
  readonly detailsPanel?: "layer-properties";
  /** Where the selection originated. Helps audit and disambiguate
   *  programmatic vs. user-initiated changes.
   *
   *  WP-12: `'keyboard'` is added so the right-rail `ToolCardDock`
   *  can clear the selection from an Esc keystroke and the audit
   *  trail still records the originating surface. */
  readonly source?:
    | "scene-tree"
    | "tool-card"
    | "engine-pick"
    | "deep-link"
    | "search"
    | "keyboard"
    | "test";
}

/** Canonical "nothing selected" payload. */
export const CLEARED_SCENE_SELECTION: SceneSelection = Object.freeze({
  kind: "cleared",
  source: "scene-tree",
});

// ---------------------------------------------------------------------------
// Pure helper builders (used by the SceneTreePanel and by the store)
// ---------------------------------------------------------------------------

/**
 * Build a selection rooted at a renderable layer.  Used by ToolCards,
 * search results, and engine-pick handlers that already know the layer
 * but not the index node.
 */
export function selectByLayerId(
  layerId: string,
  options: {
    readonly layerKind?: string;
    readonly featureId?: string | number;
    readonly featureProperties?: Readonly<Record<string, unknown>>;
    readonly sceneItemId?: string;
    readonly source?: SceneSelection["source"];
  } = {},
): SceneSelection {
  return {
    kind: "layer",
    layerId,
    layerKind: options.layerKind,
    featureId: options.featureId,
    featureProperties: options.featureProperties,
    sceneItemId: options.sceneItemId ?? layerId,
    toolCardKind: getToolCardKindFor({
      layerKind: options.layerKind,
    }),
    source: options.source ?? "scene-tree",
  };
}

/**
 * Build a selection rooted at a scene-index node id.  Used by the
 * scene-tree row click and by deep-links of the form
 * `#node=<indexNodeId>`.
 */
export function selectByIndexNodeId(
  indexNodeId: string,
  options: {
    readonly layerId?: string;
    readonly layerKind?: string;
    readonly sceneItemId?: string;
    readonly featureId?: string | number;
    readonly nodeKind?: string;
    readonly detailsPanel?: SceneSelection["detailsPanel"];
    readonly source?: SceneSelection["source"];
  } = {},
): SceneSelection {
  return {
    kind: "indexNode",
    nodeId: indexNodeId,
    indexNodeId,
    layerId: options.layerId,
    layerKind: options.layerKind,
    sceneItemId: options.sceneItemId,
    featureId: options.featureId,
    detailsPanel: options.detailsPanel,
    toolCardKind: getToolCardKindFor({
      layerKind: options.layerKind,
      indexNodeKind: options.nodeKind,
    }),
    source: options.source ?? "scene-tree",
  };
}

/** Canonical "clear selection" helper. */
export function clearSelection(
  source: SceneSelection["source"] = "scene-tree",
): SceneSelection {
  return { kind: "cleared", source };
}

/**
 * Convenience builder used by the contextual menu's "Open layer tools".
 * Combines an index node and its first renderable layer (when present)
 * into a single selection that future ToolCards can consume directly.
 */
export function selectFromTreeNode(
  node: UnifiedSceneTreeNode,
  layer: Layer | null,
  source: SceneSelection["source"] = "scene-tree",
): SceneSelection {
  const primarySceneItemId =
    layer?.id ?? node.sceneItemIds[0] ?? node.geometryRefs[0]?.sceneItemId;
  const geometryFeature =
    node.geometryRefs.find((ref) => ref.featureId != null)?.featureId ??
    node.geometryRefs.find((ref) => ref.sourceFeatureId != null)?.sourceFeatureId;
  return selectByIndexNodeId(node.id, {
    layerId: layer?.id ?? primarySceneItemId,
    layerKind:
      layer && getToolCardKindFor(layer) === "terrain"
        ? "terrain"
        : layer?.kind,
    sceneItemId: primarySceneItemId,
    featureId: geometryFeature,
    nodeKind: node.kind,
    source,
  });
}

// ---------------------------------------------------------------------------
// Panorama-bubble scene-tree bus (LoR7)
// ---------------------------------------------------------------------------

export const PANORAMA_BUBBLE_SELECT_EVENT = "viewer:panorama-bubble-select";
export const PANORAMA_BUBBLE_STATION_CHANGE_EVENT =
  "viewer:panorama-bubble-station-change";

export interface PanoramaBubbleSelectDetail {
  readonly layerId: string;
  readonly layerKind: "panorama-bubble";
  readonly stationId?: string;
  readonly source: NonNullable<SceneSelection["source"]>;
}

export interface PanoramaBubbleStationChangeDetail {
  readonly layerId: string;
  readonly stationId: string;
  readonly source: NonNullable<SceneSelection["source"]>;
}

/**
 * Build a selection for a synthetic environment layer (G34 panel / legacy
 * layer list). Opens the environment tool card without a scene-tree node.
 */
export function selectEnvironmentLayer(
  layerId: string,
  options: {
    readonly source?: SceneSelection["source"];
  } = {},
): SceneSelection {
  return selectByLayerId(layerId, {
    layerKind: "environment",
    sceneItemId: layerId,
    source: options.source ?? "tool-card",
  });
}

export function selectPanoramaBubbleLayer(
  layerId: string,
  options: {
    readonly stationId?: string;
    readonly source?: SceneSelection["source"];
  } = {},
): SceneSelection {
  return {
    kind: "layer",
    layerId,
    layerKind: "panorama-bubble",
    sceneItemId: layerId,
    panoramaStationId: options.stationId,
    toolCardKind: getToolCardKindFor({ layerKind: "panorama-bubble" }),
    source: options.source ?? "scene-tree",
  };
}

export function selectPanoramaBubbleStation(
  layerId: string,
  stationId: string,
  options: {
    readonly source?: SceneSelection["source"];
  } = {},
): SceneSelection {
  return selectPanoramaBubbleLayer(layerId, {
    stationId,
    source: options.source,
  });
}

export function dispatchPanoramaBubbleSelect(
  detail: PanoramaBubbleSelectDetail,
): PanoramaBubbleSelectDetail {
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(
        new CustomEvent<PanoramaBubbleSelectDetail>(
          PANORAMA_BUBBLE_SELECT_EVENT,
          { detail },
        ),
      );
    } catch {
      /* SSR / test environments without CustomEvent */
    }
  }
  return detail;
}

export function dispatchPanoramaBubbleStationChange(
  detail: PanoramaBubbleStationChangeDetail,
): PanoramaBubbleStationChangeDetail {
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(
        new CustomEvent<PanoramaBubbleStationChangeDetail>(
          PANORAMA_BUBBLE_STATION_CHANGE_EVENT,
          { detail },
        ),
      );
    } catch {
      /* SSR / test environments without CustomEvent */
    }
  }
  return detail;
}

// ---------------------------------------------------------------------------
// Custom-event surface for the (future) ToolCardDock
// ---------------------------------------------------------------------------

/**
 * Name of the DOM custom event dispatched whenever the user requests
 * the right-rail ToolCardDock to focus a particular tool card.
 *
 * Listeners (added by future WPs 3..10) should attach to `window`:
 *
 * ```ts
 * window.addEventListener(TOOL_CARD_REQUEST_EVENT, (e) => {
 *   const detail = (e as CustomEvent<ToolCardRequestDetail>).detail;
 *   // …open the matching card…
 * });
 * ```
 */
export const TOOL_CARD_REQUEST_EVENT = "viewer:tool-card-request";

export interface ToolCardRequestDetail {
  readonly toolCardKind: ToolCardKind;
  readonly layerKind?: string;
  readonly layerId?: string;
  readonly sceneItemId?: string;
  readonly indexNodeId?: string;
  readonly featureId?: string | number;
  readonly source: SceneSelection["source"];
}

/**
 * Dispatch a tool-card request from a `SceneSelection`. Returns the
 * built detail (so callers can also write it to the store or to an
 * audit log).  No-ops on the server (`window === undefined`).
 */
export function dispatchToolCardRequest(
  selection: SceneSelection,
): ToolCardRequestDetail | null {
  const toolCardKind = selection.toolCardKind ?? "gtwin";
  const detail: ToolCardRequestDetail = {
    toolCardKind,
    layerKind: selection.layerKind,
    layerId: selection.layerId,
    sceneItemId: selection.sceneItemId,
    indexNodeId: selection.indexNodeId ?? selection.nodeId,
    featureId: selection.featureId,
    source: selection.source ?? "scene-tree",
  };
  if (typeof window === "undefined") return detail;
  try {
    window.dispatchEvent(
      new CustomEvent<ToolCardRequestDetail>(TOOL_CARD_REQUEST_EVENT, {
        detail,
      }),
    );
  } catch {
    return detail;
  }
  return detail;
}

// Re-export the tool-card-kind union so consumers don't need a second
// import path.
export type { ToolCardKind } from "./sceneExplorer";
